import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Package, MapPin, LogOut, X, Contact, ClipboardList, ChevronRight, Calendar, Truck, CreditCard, ShoppingBag, FileText, Printer, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import Address from '../Address';
import { auth, db } from '../../utils/firebase';
import { collection, doc, getDoc, getDocs, orderBy, query, setDoc, where } from 'firebase/firestore';

const STATUS_STYLES = {
    pending:    'bg-amber-50 text-amber-700 border-amber-200',
    processing: 'bg-blue-50 text-blue-700 border-blue-200',
    completed:  'bg-green-50 text-green-700 border-green-200',
    delivered:  'bg-green-50 text-green-700 border-green-200',
    cancelled:  'bg-red-50 text-red-600 border-red-200',
    default:    'bg-slate-50 text-slate-600 border-slate-200',
};

const statusStyle = (status) =>
    STATUS_STYLES[(status || '').toLowerCase()] || STATUS_STYLES.default;

const formatDate = (val) => {
    if (!val) return '—';
    if (val?.toDate) return val.toDate().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    if (val?.seconds) return new Date(val.seconds * 1000).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    const d = new Date(val);
    return isNaN(d) ? '—' : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const rupee = (n) => `₹${Number(n || 0).toFixed(2)}`;

export default function Profile({ cart, setCart }) {
    const [activeTab, setActiveTab] = useState('account');
    const navigate = useNavigate();

    const [user, setUser] = useState({ name: '', email: '', mobile: '' });
    const [orders, setOrders] = useState([]);
    const [addresses, setAddresses] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [easyOrders, setEasyOrders] = useState([]);
    const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
    const [addressModalMode, setAddressModalMode] = useState('create');
    const [editingAddressIndex, setEditingAddressIndex] = useState(null);

    const fetchEasyOrders = async (userId) => {
        try {
            const { collection: _c, getDocs: _g } = await import('firebase/firestore');
            const snap = await _g(_c(db, 'users', userId, 'easy_orders'));
            setEasyOrders(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (e) { console.error('Error loading easy orders:', e); }
    };

    useEffect(() => {
        const unsub = auth.onAuthStateChanged(async (firebaseUser) => {
            if (firebaseUser) {
                try {
                    // Load user profile
                    let userRef = doc(db, 'users', firebaseUser.uid);
                    let userSnap = await getDoc(userRef);
                    if (!userSnap.exists()) {
                        userRef = doc(db, 'users', firebaseUser.email.toLowerCase());
                        userSnap = await getDoc(userRef);
                    }
                    if (userSnap.exists()) {
                        const d = userSnap.data();
                        setUser({
                            name:  d.fullName || `${d.firstName || ''} ${d.lastName || ''}`.trim() || '',
                            email:     firebaseUser.email,
                            mobile: d.mobileNumber || d.mobile || '',
                        });
                        setAddresses(d.addresses || []);
                    } else {
                        setUser({
                            name:  firebaseUser.displayName || '',
                            email:     firebaseUser.email,
                            mobile: '',
                        });
                    }

                    // Load orders
                    const q = query(collection(db, 'orders'), where('uid', '==', firebaseUser.uid),orderBy('createdAt', 'desc'));
                    const snap = await getDocs(q);
                    setOrders(snap.docs.map(d => ({ id: d.id, ...d.data() })));

                    fetchEasyOrders(firebaseUser.uid);
                } catch (e) {
                    console.error('Error loading profile:', e);
                }
            } else {
                navigate('/');
            }
        });
        return () => unsub();
    }, [navigate]);

    /* ─── Easy Orders ─── */
    const handleAddEasyOrderToCart = (easyOrder) => {
        if (!easyOrder.items?.length) return;
        setCart(prev => {
            let updated = [...prev];
            easyOrder.items.forEach(item => {
                const prod = { id: item.product_id, code: item.code, title: item.title, image: item.image, price: item.price };
                const idx = updated.findIndex(c => (prod.code && c.product.code === prod.code) || (!prod.code && c.product.title === prod.title));
                if (idx > -1) updated[idx] = { ...updated[idx], quantity: updated[idx].quantity + item.quantity };
                else updated.push({ product: prod, quantity: item.quantity });
            });
            return updated;
        });
        toast.success(`Added all items from "${easyOrder.name}" to cart!`);
    };

    const handleRepeatOrder = (order) => {
        if (!order.items?.length) { toast.error('No items to reorder'); return; }
        setCart(() => {
            return order.items.map(item => {
                const prod = {
                    id: item.productId,
                    title: item.productName || item.title || 'Product',
                    code: item.productCodeLetters ? `${item.productCodeLetters}${item.productCode}` : (item.productCode || item.code || ''),
                    image: item.image || '/logo.png',
                    price: Number(item.price || 0),
                };
                return { product: prod, quantity: item.cartQty || item.quantity || 1 };
            });
        });
        toast.success('Cart updated — redirecting to checkout…');
        navigate('/checkout');
    };

    const handleViewInvoice = (order) => {
        if (!order) return;
        const formattedOrder = {
            orderId: order.orderId || order.id?.slice(-8).toUpperCase() || 'ORD-BILL',
            total: Number(order.finance?.finalAmount || order.total || 0).toFixed(2),
            method: order.paymentMethod || order.method || '—',
            type: order.type || 'delivery',
            purchaseDate: formatDate(order.createdAt || order.purchaseDate),
            itemCount: order.items?.length || 0,
            items: order.items || [],
            finance: order.finance || {
                subTotal: Number(order.total || 0),
                tax: 0,
                deliveryCharge: 0,
                processingFee: 0,
                discount: 0,
                finalAmount: Number(order.total || 0)
            },
            address: order.address || {}
        };

        navigate('/order-success', { state: { orderDetails: formattedOrder } });
    };

    const handleEasyOrderCheckout = (easyOrder) => {
        if (!easyOrder.items?.length) { toast.error('No items in this template'); return; }
        setCart(() => {
            return easyOrder.items.map(item => {
                const prod = { id: item.product_id, code: item.code, title: item.title, image: item.image, price: item.price };
                return { product: prod, quantity: item.quantity };
            });
        });
        toast.success(`Cart set from "${easyOrder.name}" — redirecting to checkout…`);
        navigate('/checkout');
    };

    const handleDeleteEasyOrder = async (easyOrderId) => {
        const fu = auth.currentUser;
        if (!fu) return;
        try {
            const { deleteDoc: _del, doc: _doc } = await import('firebase/firestore');
            await _del(_doc(db, 'users', fu.uid, 'easy_orders', easyOrderId));
            toast.success('Easy Order template deleted!');
            fetchEasyOrders(fu.uid);
        } catch (e) { toast.error('Failed to delete template'); }
    };

    /* ─── Auth / Profile ─── */
    const handleLogout = () => {
        try { localStorage.removeItem('user'); } catch {}
        auth.signOut();
        window.dispatchEvent(new Event('user-login'));
        navigate('/');
        toast.success('Logged out successfully');
    };

    const handleSaveProfile = async (e) => {
        e.preventDefault();
        const fu = auth.currentUser;
        if (!fu) return;
        const nameTrimmed = (user.name || '').trim();
        if (!nameTrimmed) {
            toast.error('Name cannot be empty.');
            return;
        }
        try {
            let ref = doc(db, 'users', fu.uid);
            if (!(await getDoc(ref)).exists()) ref = doc(db, 'users', fu.email.toLowerCase());
            const parts = nameTrimmed.split(/\s+/);
            const firstName = parts[0] || '';
            const lastName = parts.slice(1).join(' ') || '';
            const mobileTrimmed = (user.mobile || '').trim();
            await setDoc(ref, { 
                firstName, 
                lastName, 
                fullName: nameTrimmed, 
                mobileNumber: mobileTrimmed,
                mobile: mobileTrimmed 
            }, { merge: true });
            localStorage.setItem('user', JSON.stringify({ fullName: nameTrimmed, email: fu.email, isLoggedIn: true }));
            window.dispatchEvent(new Event('user-login'));
            toast.success('Profile updated!');
        } catch { toast.error('Failed to update profile'); }
    };

    const handleSendPasswordReset = async () => {
        if (!user.email) {
            toast.error('No email address found.');
            return;
        }
        try {
            const { sendPasswordResetEmail } = await import('firebase/auth');
            await sendPasswordResetEmail(auth, user.email);
            toast.success('Password reset email sent! Check your inbox.');
        } catch (error) {
            console.error('Error sending password reset email:', error);
            toast.error(error.message || 'Failed to send password reset email.');
        }
    };

    const handleAddAddress = () => {
        setAddressModalMode('create');
        setEditingAddressIndex(null);
        setIsAddressModalOpen(true);
    };

    const handleEditAddress = (index) => {
        setAddressModalMode('edit');
        setEditingAddressIndex(index);
        setIsAddressModalOpen(true);
    };

    const handleDeleteAddress = async (indexToDelete) => {
        const fu = auth.currentUser;
        if (!fu) return;
        try {
            let ref = doc(db, 'users', fu.uid);
            let snap = await getDoc(ref);
            if (!snap.exists()) { ref = doc(db, 'users', fu.email.toLowerCase()); snap = await getDoc(ref); }
            if (snap.exists()) {
                const currentAddresses = snap.data().addresses || [];
                const updatedAddresses = currentAddresses.filter((_, idx) => idx !== indexToDelete);
                await setDoc(ref, { addresses: updatedAddresses }, { merge: true });
                setAddresses(updatedAddresses);
                toast.success('Address deleted successfully!');
            }
        } catch (e) {
            console.error('Error deleting address:', e);
            toast.error('Failed to delete address.');
        }
    };

    const handleAddressSave = async () => {
        const fu = auth.currentUser;
        if (fu) {
            try {
                let ref = doc(db, 'users', fu.uid);
                let snap = await getDoc(ref);
                if (!snap.exists()) { ref = doc(db, 'users', fu.email.toLowerCase()); snap = await getDoc(ref); }
                if (snap.exists()) setAddresses(snap.data().addresses || []);
            } catch {}
        }
        setIsAddressModalOpen(false);
    };

    /* ─── NAV TABS ─── */
    const tabs = [
        { key: 'account',    label: 'Account Settings',  Icon: User },
        { key: 'orders',     label: 'Order History',      Icon: Package },
        { key: 'easyorders', label: 'Easy Orders',        Icon: ClipboardList },
        { key: 'addresses',  label: 'Shipping Addresses', Icon: MapPin },
    ];

    /* ─── CONTENT ─── */
    const renderContent = () => {
        switch (activeTab) {
            /* ── Account ── */
            case 'account':
                return (
                    <div>
                        <h2 className="text-2xl font-black text-[#2C3E2B] mb-6">Account Settings</h2>
                        <form onSubmit={handleSaveProfile} className="space-y-4 max-w-2xl">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="name" className="block text-xs font-black text-slate-400 uppercase tracking-wider mb-1.5">Name</label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        autoComplete="name"
                                        className="w-full px-3.5 py-2.5 border border-slate-200 rounded-xl bg-[#f8faf8] text-sm font-semibold text-[#2C3E2B] focus:outline-none focus:ring-2 focus:ring-[#2C3E2B]/30 focus:border-[#2C3E2B]"
                                        value={user.name || ''}
                                        onChange={(e) => setUser({ ...user, name: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="mobile" className="block text-xs font-black text-slate-400 uppercase tracking-wider mb-1.5">Mobile Number</label>
                                    <input
                                        type="tel"
                                        id="mobile"
                                        name="mobile"
                                        autoComplete="tel"
                                        inputMode="tel"
                                        className="w-full px-3.5 py-2.5 border border-slate-200 rounded-xl bg-[#f8faf8] text-sm font-semibold text-[#2C3E2B] focus:outline-none focus:ring-2 focus:ring-[#2C3E2B]/30 focus:border-[#2C3E2B]"
                                        value={user.mobile || ''}
                                        onChange={(e) => setUser({ ...user, mobile: e.target.value })}
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-xs font-black text-slate-400 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                                        Email Address <span className="text-[9px] font-bold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-full normal-case tracking-normal">🔒 locked</span>
                                    </label>
                                    <div className="w-full px-3.5 py-2.5 border border-slate-200 rounded-xl bg-slate-50 text-sm text-slate-400 select-none cursor-not-allowed">{user.email || '—'}</div>
                                    <p className="text-[11px] text-slate-400 mt-1">Email cannot be changed after registration.</p>
                                </div>
                            </div>
                            <div className="pt-2">
                                <button
                                    type="button"
                                    onClick={handleSendPasswordReset}
                                    className="text-sm font-bold text-[#2C3E2B] hover:text-[#1e2d1e] underline bg-transparent border-none p-0 cursor-pointer transition-all"
                                >
                                    Send password reset email
                                </button>
                            </div>
                            <button type="submit" className="mt-4 px-6 py-2.5 bg-[#2C3E2B] text-white font-extrabold text-sm rounded-xl hover:bg-[#1e2d1e] transition shadow-sm hover:shadow-md hover:-translate-y-0.5">
                                Save Changes
                            </button>
                        </form>
                    </div>
                );

            /* ── Orders ── */
            case 'orders':
                return (
                    <div>
                        <h2 className="text-2xl font-black text-[#2C3E2B] mb-6">Order History</h2>
                        {orders.length === 0 ? (
                            <div className="text-center py-12 bg-[#f8faf8] rounded-2xl border border-slate-100">
                                <ShoppingBag className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                                <p className="font-bold text-slate-500">No orders placed yet.</p>
                                <p className="text-xs text-slate-400 mt-1">Your order history will appear here.</p>
                            </div>
                        ) : (
                            <div className="space-y-3 bg-[#f4f6f4] rounded-2xl p-4">
                                {orders.map((order, idx) => {
                                    const finalAmt = Number(order.finance?.finalAmount || order.total || 0);
                                    const status   = order.status || order.type || 'pending';
                                    const itemCount = order.items?.length || order.itemCount || 0;
                                    const payMethod = order.paymentMethod || order.method || '—';
                                    return (
                                        <div
                                            key={order.id || idx}
                                            onClick={() => setSelectedOrder(order)}
                                            className="bg-white border border-slate-100 rounded-2xl p-5 hover:shadow-md transition-all cursor-pointer group hover:border-[#2C3E2B]/20"
                                        >
                                            <div className="flex justify-between items-start">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                        <span className="text-sm font-black text-[#2C3E2B]">
                                                            #{order.orderId || order.id?.slice(-8).toUpperCase() || `ORD-${idx + 1}`}
                                                        </span>
                                                        <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full border ${statusStyle(status)}`}>
                                                            {status}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-3 text-xs text-slate-400 font-semibold flex-wrap mt-1">
                                                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(order.createdAt)}</span>
                                                        <span className="flex items-center gap-1"><Package className="w-3 h-3" />{itemCount} item{itemCount !== 1 ? 's' : ''}</span>
                                                        <span className="flex items-center gap-1"><CreditCard className="w-3 h-3" />{payMethod}</span>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2 shrink-0">
                                                    <span className="text-base font-black text-[#9E2A2B]">{rupee(finalAmt)}</span>
                                                    <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-[#2C3E2B] transition" />
                                                </div>
                                            </div>
                                            {/* Item previews */}
                                            {order.items?.length > 0 && (
                                                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between gap-2 flex-wrap">
                                                    <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
                                                        {order.items.slice(0, 4).map((item, i) => (
                                                            <span key={i} className="text-[10px] font-semibold text-slate-500 bg-slate-50 border border-slate-200 px-2 py-0.5 rounded-full truncate max-w-[140px]">
                                                                {item.productName || item.title || 'Item'}
                                                            </span>
                                                        ))}
                                                        {order.items.length > 4 && (
                                                            <span className="text-[10px] font-bold text-slate-400">+{order.items.length - 4} more</span>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-2 flex-wrap shrink-0">
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); handleViewInvoice(order); }}
                                                            className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-[#2C3E2B] text-[10px] font-black rounded-lg border-none cursor-pointer transition shrink-0"
                                                        >
                                                            <FileText className="w-3 h-3" /> View Invoice
                                                        </button>
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); handleRepeatOrder(order); }}
                                                            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#2C3E2B] text-white text-[10px] font-black rounded-lg border-none cursor-pointer hover:bg-[#1e2d1e] transition shrink-0"
                                                        >
                                                            Repeat Order
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );

            /* ── Easy Orders ── */
            case 'easyorders':
                return (
                    <div>
                        <h2 className="text-2xl font-black text-[#2C3E2B] mb-6">My Easy Orders</h2>
                        {easyOrders.length === 0 ? (
                            <div className="text-center py-12 bg-[#f8faf8] rounded-2xl border border-slate-100">
                                <ClipboardList className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                                <p className="font-bold text-slate-500">No Easy Order templates yet.</p>
                                <p className="text-xs text-slate-400 mt-1">Save your cart as a template from the Cart page.</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {easyOrders.map((t, idx) => (
                                    <div key={idx} className="bg-white border border-slate-100 rounded-2xl p-5 shadow-xs">
                                        <div className="flex justify-between items-start mb-3 flex-wrap gap-2">
                                            <div>
                                                <h3 className="text-sm font-extrabold text-[#2C3E2B]">{t.name}</h3>
                                                <p className="text-xs text-slate-400 mt-0.5">Saved {formatDate(t.createdAt)} · {t.items?.length || 0} items</p>
                                            </div>
                                            <div className="flex gap-2 flex-wrap">
                                                <button onClick={() => handleAddEasyOrderToCart(t)} className="px-3 py-1.5 bg-slate-100 text-[#2C3E2B] text-xs font-black rounded-xl border-none cursor-pointer hover:bg-slate-200 transition">
                                                    Add to Cart
                                                </button>
                                                <button onClick={() => handleEasyOrderCheckout(t)} className="px-3 py-1.5 bg-[#2C3E2B] text-white text-xs font-black rounded-xl border-none cursor-pointer hover:bg-[#1e2d1e] transition">
                                                    Checkout Now
                                                </button>
                                                <button onClick={() => handleDeleteEasyOrder(t.id)} className="px-3 py-1.5 bg-red-50 text-red-600 text-xs font-black rounded-xl border-none cursor-pointer hover:bg-red-100 transition">
                                                    Delete
                                                </button>
                                            </div>
                                        </div>
                                        <div className="space-y-2 bg-[#f8faf8] rounded-xl p-3 border border-slate-100 max-h-48 overflow-y-auto">
                                            {t.items?.map((item, i) => (
                                                <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-slate-100 last:border-b-0">
                                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                                        <img src={item.image} alt={item.title} className="w-7 h-7 object-contain rounded bg-white border border-slate-200 p-0.5 shrink-0" onError={e => { e.target.src = '/logo.png'; }} />
                                                        <span className="font-semibold text-[#2C3E2B] truncate">{item.title}</span>
                                                    </div>
                                                    <span className="font-bold text-slate-500 ml-4 shrink-0">Qty: {item.quantity}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );

            /* ── Addresses ── */
            case 'addresses':
                return (
                    <div>
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-2xl font-black text-[#2C3E2B]">Shipping Addresses</h2>
                            <button onClick={handleAddAddress} className="flex items-center gap-2 bg-[#2C3E2B] text-white px-4 py-2.5 rounded-xl font-extrabold text-sm hover:bg-[#1e2d1e] transition shadow-sm hover:shadow-md hover:-translate-y-0.5">
                                <Contact size={16} /> Add Address
                            </button>
                        </div>
                        {addresses.length === 0 ? (
                            <div className="text-center py-12 bg-[#f8faf8] rounded-2xl border border-slate-100">
                                <MapPin className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                                <p className="font-bold text-slate-500">No saved addresses yet.</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {addresses.map((addr, idx) => (
                                    <div key={idx} className="bg-white border border-slate-100 rounded-2xl p-5 hover:shadow-md transition flex flex-col justify-between">
                                        <div>
                                            <div className="flex items-center gap-2 mb-3">
                                                <MapPin className="w-4 h-4 text-[#2C3E2B]" />
                                                <h3 className="font-extrabold text-[#2C3E2B] text-sm">{addr.fullName}</h3>
                                            </div>
                                            <div className="text-xs text-slate-500 space-y-1 font-semibold">
                                                <p>{addr.streetAddress}{addr.unitNumber ? `, Unit ${addr.unitNumber}` : ''}</p>
                                                <p>{addr.city}, {addr.state} {addr.zipCode}</p>
                                                {addr.country && <p>{addr.country}</p>}
                                                <p className="pt-1 text-slate-600">📞 {addr.mobileNumber}</p>
                                                {addr.email && <p className="text-slate-600">✉️ {addr.email}</p>}
                                                {addr.gstin && <p className="text-slate-600 font-bold">GSTIN: {addr.gstin}</p>}
                                            </div>
                                        </div>
                                        <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
                                            <button
                                                onClick={() => handleEditAddress(idx)}
                                                className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-[#2C3E2B] text-xs font-bold rounded-xl transition border-none cursor-pointer"
                                            >
                                                <Pencil size={14} /> Edit
                                            </button>
                                            <button
                                                onClick={() => handleDeleteAddress(idx)}
                                                className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 text-xs font-bold rounded-xl transition border-none cursor-pointer"
                                            >
                                                <Trash2 size={14} /> Delete
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );

            default: return null;
        }
    };

    return (
        <div className="bg-[#F8F7F3] min-h-screen py-10">
            <div className="max-w-[1700px] mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">

                    {/* ── Sidebar ── */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-3xl shadow-xs border border-slate-100 overflow-hidden sticky top-24">
                            {/* Avatar */}
                            <div className="px-6 pt-7 pb-5 text-center border-b border-slate-100">
                                <div className="w-16 h-16 rounded-2xl bg-[#ECEEEB] mx-auto flex items-center justify-center mb-3">
                                    <User className="w-8 h-8 text-[#2C3E2B]/50" />
                                </div>
                                <h3 className="font-extrabold text-[#2C3E2B] text-base leading-tight">{user.name}</h3>
                                <p className="text-xs text-slate-400 font-semibold mt-0.5 truncate">{user.email}</p>
                            </div>
                            {/* Nav */}
                            <div className="p-3 space-y-1">
                                {tabs.map(({ key, label, Icon }) => (
                                    <button
                                        key={key}
                                        onClick={() => setActiveTab(key)}
                                        className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-bold transition-all cursor-pointer border-none ${
                                            activeTab === key
                                                ? 'bg-[#2C3E2B] text-white shadow-sm'
                                                : 'text-slate-600 hover:bg-[#ECEEEB] hover:text-[#2C3E2B]'
                                        }`}
                                    >
                                        <Icon className="w-4 h-4 shrink-0" />
                                        {label}
                                    </button>
                                ))}
                                <div className="h-px bg-slate-100 my-1 mx-2" />
                                <button
                                    onClick={handleLogout}
                                    className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-bold text-red-600 hover:bg-red-50 transition-all cursor-pointer border-none"
                                >
                                    <LogOut className="w-4 h-4 shrink-0" /> Logout
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* ── Content ── */}
                    <div className="lg:col-span-3">
                        <div className="bg-white rounded-3xl shadow-xs border border-slate-100 p-8 min-h-[500px]">
                            {renderContent()}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Order Detail Modal ── */}
            {selectedOrder && (() => {
                const o = selectedOrder;
                const finance = o.finance || {};
                const status  = o.status || o.type || 'pending';
                return (
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[88vh] flex flex-col overflow-hidden">
                            {/* Header */}
                            <div className="bg-[#2C3E2B] px-6 py-5 flex items-center justify-between flex-shrink-0">
                                <div>
                                    <h3 className="text-base font-black text-white m-0">
                                        Order #{o.orderId || o.id?.slice(-8).toUpperCase()}
                                    </h3>
                                    <p className="text-[11px] text-white/60 font-semibold m-0">{formatDate(o.createdAt)}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button 
                                        onClick={() => handleViewInvoice(o)} 
                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-black rounded-xl border-none cursor-pointer transition"
                                    >
                                        <Printer className="w-3.5 h-3.5" /> View Invoice
                                    </button>
                                    <span className={`text-[10px] font-black uppercase px-2.5 py-1 rounded-full border ${statusStyle(status)}`}>{status}</span>
                                    <button onClick={() => setSelectedOrder(null)} className="w-8 h-8 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition border-none cursor-pointer">
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            {/* Body */}
                            <div className="flex-1 overflow-y-auto p-6 space-y-5">
                                {/* Finance summary */}
                                <div className="bg-[#f8faf8] border border-slate-100 rounded-2xl p-4 space-y-2">
                                    {[
                                        ['Subtotal',       rupee(finance.subTotal)],
                                        finance.discount > 0 && ['Discount', `-${rupee(finance.discount)}`],
                                        ['Tax',            rupee(finance.tax)],
                                        ['Shipping',       rupee(finance.deliveryCharge)],
                                        finance.processingFee > 0 && ['Processing Fee', rupee(finance.processingFee)],
                                    ].filter(Boolean).map(([label, value]) => (
                                        <div key={label} className="flex justify-between text-xs font-semibold text-slate-500">
                                            <span>{label}</span><span className="font-bold text-[#2C3E2B]">{value}</span>
                                        </div>
                                    ))}
                                    <div className="border-t border-slate-200 pt-2 flex justify-between text-sm font-black">
                                        <span className="text-[#2C3E2B]">Total</span>
                                        <span className="text-[#9E2A2B]">{rupee(finance.finalAmount || o.total)}</span>
                                    </div>
                                </div>

                                {/* Payment & delivery */}
                                <div className="grid grid-cols-2 gap-3">
                                    {[
                                        { label: 'Payment', value: o.paymentMethod || o.method || '—', Icon: CreditCard },
                                        { label: 'Delivery', value: o.address?.fullName ? `${o.address.city || ''}` : (o.type || '—'), Icon: Truck },
                                    ].map(({ label, value, Icon }) => (
                                        <div key={label} className="bg-[#f8faf8] border border-slate-100 rounded-xl p-3">
                                            <div className="flex items-center gap-1.5 mb-1">
                                                <Icon className="w-3.5 h-3.5 text-slate-400" />
                                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">{label}</span>
                                            </div>
                                            <p className="text-xs font-extrabold text-[#2C3E2B]">{value}</p>
                                        </div>
                                    ))}
                                </div>

                                {/* Items */}
                                <div>
                                    <span className="text-[11px] font-black text-slate-400 uppercase tracking-wider block mb-3">Items Ordered</span>
                                    {o.items?.length > 0 ? (
                                        <div className="space-y-2">
                                            {o.items.map((item, i) => {
                                                const qty   = item.cartQty || item.quantity || 1;
                                                const price = Number(item.price || 0);
                                                const code  = item.productCodeLetters ? `${item.productCodeLetters}${item.productCode}` : (item.productCode || item.code || '');
                                                return (
                                                    <div key={i} className="flex items-start justify-between bg-[#f8faf8] border border-slate-100 rounded-xl px-4 py-3">
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-xs font-extrabold text-[#2C3E2B] leading-snug">{item.productName || item.title || 'Item'}</p>
                                                            <p className="text-[10px] text-slate-400 font-semibold mt-0.5">
                                                                {code && `Code: ${code} · `}Qty: {qty}
                                                            </p>
                                                        </div>
                                                        {price > 0 && (
                                                            <p className="text-xs font-black text-[#9E2A2B] ml-4 shrink-0">{rupee(price * qty)}</p>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <p className="text-xs text-slate-400 text-center py-4">No item details available.</p>
                                    )}
                                </div>

                                {/* Delivery address */}
                                {o.address && (
                                    <div>
                                        <span className="text-[11px] font-black text-slate-400 uppercase tracking-wider block mb-2">Delivery Address</span>
                                        <div className="bg-[#f8faf8] border border-slate-100 rounded-xl px-4 py-3 text-xs text-slate-600 font-semibold space-y-0.5">
                                            <p className="font-extrabold text-[#2C3E2B]">{o.address.fullName}</p>
                                            <p>{o.address.streetAddress}{o.address.unitNumber ? `, ${o.address.unitNumber}` : ''}</p>
                                            <p>{o.address.city}, {o.address.state} {o.address.zipCode}</p>
                                            {o.address.mobileNumber && <p>📞 {o.address.mobileNumber}</p>}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Address Modal */}
            <Address isOpen={isAddressModalOpen} onClose={() => setIsAddressModalOpen(false)} mode={addressModalMode} addressIndex={editingAddressIndex} onSave={handleAddressSave} />
        </div>
    );
}
